'use client'

import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { useEffect, useId, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ConfirmDialogProps = {
  isOpen: boolean
  title: string
  message: ReactNode
  yesLabel?: string
  noLabel?: string
  dir?: 'ltr' | 'rtl'
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  yesLabel = 'Yes',
  noLabel = 'No',
  dir = 'ltr',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [mounted, setMounted] = useState(false)
  const titleId = useId()

  useEffect(() => setMounted(true), [])
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  const dialog = (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        dir={dir}
        className="w-full max-w-sm rounded-md bg-white p-6 shadow-lg"
      >
        <h2 id={titleId} className="font-bebas-neue mb-3 text-2xl uppercase tracking-[1px] text-[#111]">
          {title}
        </h2>
        <p className="font-poppins mb-6 text-sm text-neutral-700">{message}</p>
        <div className={`flex gap-3 ${dir === 'rtl' ? 'justify-start' : 'justify-end'}`}>
          <button
            type="button"
            className="font-poppins rounded border border-neutral-300 px-4 py-2 text-sm font-medium text-[#111] hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000] focus-visible:ring-offset-2"
            onClick={onCancel}
          >
            {noLabel}
          </button>
          <button
            type="button"
            className="font-poppins rounded bg-[#FF0000] px-4 py-2 text-sm font-medium text-white hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000] focus-visible:ring-offset-2"
            onClick={onConfirm}
          >
            {yesLabel}
          </button>
        </div>
      </div>
    </div>
  )

  if (mounted && typeof document !== 'undefined') {
    return createPortal(dialog, document.body)
  }

  return dialog
}
